import {
  FuturaPTDemi,
  InterMedium as InterMediumFont,
  InterRegular as InterRegularFont,
  shadows,
} from '@airship-platform/jam-design-tokens';
import '@mui/lab/themeAugmentation';
import { Theme, ThemeOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    buttonSm: React.CSSProperties;
    buttonMd: React.CSSProperties;
    buttonLg: React.CSSProperties;
    body3: React.CSSProperties;
  }
  interface Components {
    MuiDataGrid?: {
      defaultProps?: Record<string, unknown>;
      styleOverrides?: {
        root?:
          | React.CSSProperties
          | ((props: { theme: Theme }) => React.CSSProperties);
      };
      variants?: unknown[];
    };
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    red?: true;
    gray?: true;
    blue?: true;
    green?: true;
    gold?: true;
    softRed?: true;
    softGray?: true;
    softBlue?: true;
    softGold?: true;
  }
  interface ButtonPropsVariantOverrides {
    link: true;
  }
}

declare module '@mui/material/Drawer' {
  interface DrawerProps {
    navbar?: 'default';
  }
}

declare module '@mui/material/Switch' {
  interface SwitchPropsColorOverrides {
    tertiary?: true;
  }
}

declare module '@mui/material/Radio' {
  interface RadioPropsColorOverrides {
    tertiary?: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    frosted: true;
  }
  interface ChipPropsSizeOverrides {
    large: true;
  }
}

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    bordered: true;
    float: true;
    frosted: true;
    plain: true;
  }
}

declare module '@mui/material/CardMedia' {
  interface CardMediaOwnProps {
    variant?: 'tinted' | 'cover';
  }
}

declare module '@mui/material/Tabs' {
  interface TabsPropsVariantOverrides {
    pill: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonOwnProps {
    variant?: 'text' | 'outlined';
  }
  interface IconButtonPropsColorOverrides {
    red?: true;
    gray?: true;
    blue?: true;
    gold?: true;
    softRed?: true;
    softGray?: true;
    softBlue?: true;
    softGold?: true;
  }
}

const frostedFill = 'rgba(255, 255, 255, 0.35)';

const futuraPT = {
  fontFamily: 'Futura PT',
  fontStyle: 'normal',
  fontWeight: 500,
  src: `
    url(${FuturaPTDemi}) format('truetype')
  `,
};

const interRegular = {
  fontFamily: 'Inter',
  fontStyle: 'normal',
  fontWeight: 400,
  src: `
    url(${InterRegularFont}) format('truetype')
  `,
};

const interMedium = {
  fontFamily: 'Inter',
  fontStyle: 'normal',
  fontWeight: 500,
  src: `
    url(${InterMediumFont}) format('truetype')
  `,
};

export const baseComponents: ThemeOptions['components'] = {
  MuiCssBaseline: {
    styleOverrides: {
      html: [
        { '@font-face': futuraPT },
        { '@font-face': interRegular },
        { '@font-face': interMedium },
      ],
    },
  },
  MuiDialog: {
    defaultProps: {
      maxWidth: 'md',
    },
    variants: [
      {
        props: { maxWidth: 'md' },
        style: {
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '650px',
            },
          },
        },
      },
    ],
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: '0',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '1rem 2rem',
      },
    },
  },
  MuiTypography: {
    defaultProps: {
      variant: 'body1',
      component: 'p',
      color: 'inherit',
    },
    styleOverrides: {
      root: { display: 'inline-block' },
    },
  },
  MuiCard: {
    defaultProps: {},
    styleOverrides: {
      root: {
        borderRadius: '0.5rem',
        padding: '1rem',
      },
    },
    variants: [
      {
        props: { variant: 'bordered' },
        style: { borderRadius: '1rem' },
      },
      {
        props: { variant: 'float' },
        style: {
          borderRadius: '1rem',
          backgroundColor: 'transparent',
          border: 'none',
          boxShadow: 'none',
        },
      },
      {
        props: { variant: 'frosted' },
        style: { borderRadius: '1rem' },
      },
      {
        props: { variant: 'plain' },
        style: { padding: 0 },
      },
    ],
  },
  MuiCardMedia: {
    styleOverrides: {
      root: ({ theme }) => ({
        display: 'flex',
        flex: 1,
        gap: '0.5rem',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '12.5rem',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        backgroundColor: theme.palette.gray?.[50],
        '& > img': {
          backgroundColor: 'transparent',
          mixBlendMode: 'multiply',
        },
      }),
    },
    variants: [
      {
        props: { variant: 'tinted' },
        style: { flex: 'none', minWidth: 0 },
      },
      {
        props: { variant: 'cover' },
        style: {
          flex: 'none',
          minWidth: 0,
          backgroundColor: 'transparent',
          '& > img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            mixBlendMode: 'normal',
          },
        },
      },
    ],
  },
  MuiCircularProgress: {
    defaultProps: {
      color: 'inherit',
    },
  },
  MuiSkeleton: {
    defaultProps: {
      variant: 'rounded',
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      fullWidth: true,
      color: 'secondary',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '0.5rem',
        '& .MuiOutlinedInput-root': {
          borderRadius: '0.5rem',
        },
        '& .MuiInputLabel-root': {
          fontSize: theme.typography.body2.fontSize,
        },
        '& input': {
          fontSize: theme.typography.body2.fontSize,
        },
        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
          {
            WebkitAppearance: 'none',
            margin: 0,
          },
        '& input[type=number]': {
          MozAppearance: 'textfield',
        },
      }),
    },
    variants: [
      {
        props: { select: true },
        style: {
          '& .MuiSelect-select': {
            padding: '0.875rem 1rem',
          },
        },
      },
      {
        props: { select: true, size: 'small' },
        style: {
          '& .MuiSelect-select': {
            padding: '0.5rem 1rem',
          },
        },
      },
    ],
  },
  MuiTabs: {
    defaultProps: { textColor: 'secondary' },
    styleOverrides: {
      root: ({ theme }) => ({
        border: 'none',
        '& .MuiTabs-indicator': {
          backgroundColor: theme.palette.gold?.[600],
        },
      }),
    },
    variants: [
      {
        props: { variant: 'pill' },
        style: ({ theme }) => ({
          minHeight: '3rem',
          '& .MuiTabs-indicator': {
            display: 'none',
          },
          '& .MuiTabs-list': {
            gap: '0.25rem',
          },
          '& .MuiTab-root': {
            minHeight: '3rem',
            padding: '0 1rem',
            borderRadius: '3rem',
            textTransform: 'none',
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: theme.palette.gray?.[100],
            },
            [theme.breakpoints.up('sm')]: {
              padding: '0 1.5rem',
            },
          },
          '& .MuiTab-root.Mui-selected': {
            backgroundColor: theme.palette.gray?.[700],
            color: theme.palette.common.white,
          },
          '&.MuiTabs-vertical .MuiTab-root': {
            alignItems: 'flex-start',
            maxWidth: 'none',
          },
        }),
      },
    ],
  },
  MuiAppBar: {
    defaultProps: {
      color: 'inherit',
    },
    variants: [
      {
        props: { variant: 'float' },
        style: ({ theme }) => ({
          width: 'auto',
          top: 0,
          left: 0,
          right: 0,
          margin: '0.5rem 0.75rem 0',
          borderRadius: '0.5rem',
          [theme.breakpoints.up('sm')]: {
            margin: '0.75rem 1rem 0',
            borderRadius: '1rem',
          },
        }),
      },
    ],
  },
  MuiTab: {
    defaultProps: { nativeButton: false },
    styleOverrides: {
      root: ({ theme }) => ({
        textTransform: 'none',
        fontFamily: theme.typography.body2.fontFamily,
        fontSize: theme.typography.body2.fontSize,
        fontWeight: theme.typography.body2.fontWeight,
        padding: '0.38rem 1rem',
        '&.Mui-selected': {
          backgroundColor: theme.palette.gold?.[50],
        },
      }),
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiFormControlLabel-label': {
          fontSize: theme.typography.subtitle2.fontSize,
          fontFamily: theme.typography.subtitle2.fontFamily,
          fontWeight: theme.typography.subtitle2.fontWeight,
        },
      }),
    },
  },
  MuiIconButton: {
    defaultProps: {
      color: 'inherit',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.common.white,
        boxShadow: 'none',
        '&:disabled': {
          borderColor: theme.palette.gray?.[400],
          backgroundColor: theme.palette.gray?.[50],
          color: theme.palette.gray?.[400],
        },
      }),
    },
    variants: [
      {
        props: { variant: 'text' },
        style: ({ theme }) => ({
          backgroundColor: 'transparent',
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: theme.palette.gray?.[100],
          },
        }),
      },
      {
        props: { color: 'softGold' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.softGold?.[50],
          color: theme.palette.softGold?.[700],
          '&:hover': {
            backgroundColor: theme.palette.softGold?.[100],
          },
        }),
      },
      {
        props: { color: 'softBlue' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.softBlue?.[50],
          color: theme.palette.softBlue?.[600],
          '&:hover': {
            backgroundColor: theme.palette.softBlue?.[100],
          },
        }),
      },
      {
        props: { color: 'softRed' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.softRed?.[50],
          color: theme.palette.softRed?.[600],
          '&:hover': {
            backgroundColor: theme.palette.softRed?.[100],
          },
        }),
      },
      {
        props: { color: 'softGray' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.softGray?.[50],
          color: theme.palette.softGray?.[700],
          '&:hover': {
            backgroundColor: theme.palette.softGray?.[100],
          },
        }),
      },
      {
        props: { color: 'gold' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.gold?.[500],
          color: theme.palette.common.white,
          border: `1px solid ${theme.palette.gold?.[600]}`,
          '&:hover': {
            backgroundColor: theme.palette.gold?.[400],
          },
        }),
      },
      {
        props: { color: 'gray' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.gray?.[700],
          color: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.gray?.[600],
          },
        }),
      },
      {
        props: { variant: 'outlined' },
        style: ({ theme }) => ({
          backgroundColor: 'transparent',
          border: `1px solid ${theme.palette.gray?.[300]}`,
          color: theme.palette.gray?.[700],
          '&:hover': {
            backgroundColor: theme.palette.gray?.[100],
          },
        }),
      },
    ],
  },
  MuiChip: {
    defaultProps: {
      size: 'medium',
      color: 'default',
    },
    variants: [
      {
        props: { variant: 'frosted' },
        style: { backgroundColor: frostedFill },
      },
      {
        props: { size: 'large' },
        style: ({ theme }) => ({
          height: '2.75rem',
          borderRadius: '3rem',
          padding: `0 ${theme.spacing(3)}`,
          gap: theme.spacing(1),
          fontSize: theme.typography.subtitle2.fontSize,
          lineHeight: theme.typography.subtitle2.lineHeight,
          '& .MuiChip-label': { padding: 0 },
          '& .MuiChip-icon': {
            margin: 0,
            width: '1rem',
            height: '1rem',
            fontSize: '1rem',
          },
        }),
      },
    ],
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '0.25rem 0.5rem',
        fontFamily: theme.typography.caption.fontFamily,
        fontWeight: theme.typography.caption.fontWeight,
      }),
      icon: {
        color: 'inherit',
      },
    },
  },
  MuiCheckbox: {
    defaultProps: {},
    styleOverrides: {
      root: ({ theme }) => ({
        '&.Mui-checked': {
          color: theme.palette.blue?.[500],
        },
      }),
    },
  },
  MuiRadio: {
    defaultProps: {
      color: 'tertiary',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        '&.Mui-checked': {
          color: theme.palette.blue?.[500],
        },
      }),
    },
  },
  MuiLink: {
    defaultProps: {
      color: 'primary',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        cursor: 'pointer',
        color: theme.palette.blue?.[500],
        textDecorationColor: theme.palette.blue?.[500],
      }),
    },
  },
  MuiInputBase: {
    defaultProps: {
      color: 'secondary',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.common.white,
        color: theme.palette.text.primary,
        '& input': {
          fontFamily: theme.typography.button.fontFamily,
          fontWeight: theme.typography.button.fontWeight,
          fontSize: theme.typography.button.fontSize,
        },
        '& textarea': {
          fontFamily: theme.typography.button.fontFamily,
          fontWeight: theme.typography.button.fontWeight,
          fontSize: theme.typography.button.fontSize,
        },
      }),
    },
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.common.white,
        boxShadow: shadows.md,
        borderRadius: 0,
      }),
    },
    variants: [
      {
        props: { variant: 'bordered' },
        style: ({ theme }) => ({
          borderRadius: '1rem',
          backgroundColor: theme.palette.common.white,
          border: `1px solid ${theme.palette.grey[300]}`,
          boxShadow: '0px 1px 2px 0px rgba(21, 21, 21, 0.08)',
        }),
      },
      {
        props: { variant: 'frosted' },
        style: {
          borderRadius: '1rem',
          backgroundColor: frostedFill,
          border: 'none',
          boxShadow: 'none',
        },
      },
      {
        props: { variant: 'plain' },
        style: {
          backgroundColor: 'transparent',
          border: 'none',
          boxShadow: 'none',
        },
      },
    ],
  },
  MuiAvatar: {
    defaultProps: {
      variant: 'circular',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        width: '2.5rem',
        height: '2.5rem',
        backgroundColor: theme.palette.blue?.[50],
        '& p': {
          color: theme.palette.blue?.[600],
          fontSize: theme.typography.caption.fontSize,
          fontWeight: 500,
        },
      }),
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '0.5rem',
        '& .MuiSelect-select': {
          borderRadius: '4rem',
          fontSize: theme.typography.caption.fontSize,
          '&:focus': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
        },
      }),
    },
    variants: [
      {
        props: { variant: 'standard' },
        style: () => ({
          '&.MuiInput-root:before': {
            borderBottom: 'none',
          },
          '&.MuiInput-root:after': {
            borderBottom: 'none',
          },
          '& .MuiSelect-select': {
            border: 0,
          },
          '&.MuiInput-root:hover:not(.Mui-disabled):before': {
            borderBottom: 'none',
          },
        }),
      },
    ],
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: '0.5rem',
        backgroundColor: theme.palette.common.white,
        padding: '0',
        '& .MuiMenuItem-root': {
          fontSize: theme.typography.caption.fontSize,
          fontWeight: 500,
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          '&:last-child': {
            borderBottom: 'none',
          },
        },
      }),
    },
  },
  MuiMenuItem: {
    defaultProps: {
      color: 'secondary',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.common.white,
        '&.Mui-selected': {
          backgroundColor: `${theme.palette.blue?.[50]} !important`,
          '&:hover': {
            backgroundColor: theme.palette.blue?.[50],
          },
        },
        '&.Mui-selected.Mui-focusVisible': {
          '&:hover': {
            backgroundColor: theme.palette.blue?.[50],
          },
        },
        '&:focus': {
          backgroundColor: theme.palette.blue?.[50],
        },
      }),
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.common.white,
        boxShadow: 'none',
        borderRadius: '0.5rem',
        '&.Mui-expanded::before': { opacity: '1' },
      }),
    },
    variants: [
      {
        props: { variant: 'plain' },
        style: ({ theme }) => ({
          backgroundColor: 'transparent',
        }),
      },
    ],
  },
  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        padding: '0.5rem 1rem',
      },
    },
  },
  MuiButton: {
    defaultProps: {
      size: 'medium',
      color: 'primary',
      variant: 'contained',
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 800,
        boxShadow: 'none',
        borderRadius: '3rem',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        minWidth: 'auto',
      },
    },
    variants: [
      {
        props: { variant: 'link' },
        style: {
          textDecoration: 'underline',
          minWidth: 'auto',
          padding: 0,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
          },
        },
      },
      {
        props: { size: 'small' },
        style: ({ theme }) => ({
          padding: '0.12rem 0.75rem',
          fontSize: theme.typography.buttonSm.fontSize,
          lineHeight: theme.typography.buttonSm.lineHeight,
          fontWeight: theme.typography.buttonSm.fontWeight,
          minHeight: '32px',
          '.MuiCircularProgress-root, .circular-progress-loading': {
            width: '1.25rem !important',
            height: '1.25rem !important',
          },
          svg: {
            width: '1.25rem',
            height: '1.25rem',
            color: 'inherit',
          },
        }),
      },
      {
        props: { size: 'medium' },
        style: ({ theme }) => ({
          padding: '0.25rem 1rem',
          fontSize: theme.typography.buttonMd.fontSize,
          lineHeight: theme.typography.buttonMd.lineHeight,
          fontWeight: theme.typography.buttonMd.fontWeight,
          minHeight: '40px',
          '.MuiCircularProgress-root, .circular-progress-loading': {
            width: '1.5rem !important',
            height: '1.5rem !important',
          },
          svg: {
            width: '1.5rem',
            height: '1.5rem',
            color: 'inherit',
          },
        }),
      },
      {
        props: { size: 'large' },
        style: ({ theme }) => ({
          padding: '0.38rem 1.5rem',
          fontSize: theme.typography.buttonLg.fontSize,
          lineHeight: theme.typography.buttonLg.lineHeight,
          fontWeight: theme.typography.buttonLg.fontWeight,
          minHeight: '48px',
          '.MuiCircularProgress-root, .circular-progress-loading': {
            width: '1.75rem !important',
            height: '1.75rem !important',
          },
          svg: {
            width: '1.75rem',
            height: '1.75rem',
            color: 'inherit',
          },
        }),
      },
      {
        props: { variant: 'outlined' },
        style: ({ theme }) => ({
          borderWidth: '1px',
          '&:disabled': {
            borderColor: theme.palette.gray?.[300],
            color: theme.palette.gray?.[300],
          },
        }),
      },
      {
        props: { variant: 'contained' },
        style: ({ theme }) => ({
          '&:hover': {
            boxShadow: 'none',
          },
          '&:disabled': {
            backgroundColor: theme.palette.gray?.[50],
            color: theme.palette.gray?.[300],
          },
        }),
      },
      {
        props: { variant: 'text' },
        style: ({ theme }) => ({
          padding: '0.5rem',
          '&:disabled': {
            color: theme.palette.gray?.[400],
          },
        }),
      },
      {
        props: { variant: 'text', color: 'gray' },
        style: ({ theme }) => ({
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: theme.palette.gray?.[100],
          },
        }),
      },
    ],
  },
  MuiAlert: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '0.5rem 0.75rem',
        fontSize: theme.typography.caption.fontSize,
        borderRadius: '0.5rem',
        alignItems: 'center',
        '& .MuiAlert-icon': {
          fontSize: '1.25rem',
          padding: 0,
          marginRight: '0.5rem',
        },
        '& .MuiAlert-message': {
          padding: 0,
          fontSize: theme.typography.caption.fontSize,
        },
        '& .MuiAlert-action': {
          padding: 0,
          paddingLeft: '0.5rem',
          marginRight: 0,
        },
      }),
    },
  },
};
